import React, { useEffect, useState } from 'react';
import LazyLoad from 'react-lazyload';
import pickBy from 'lodash/pickBy';
import { useStyles, cx } from '../../../lib/styles';
import { withQuery } from '../../../lib/stringUtils';
import { Color, Corner, Brand } from '../../../lib/theme';

import imageStyles from './Image_styles';

export interface ImageProps {
  /** The brand to use for colouring the background of the image. */
  brand?: Brand;

  /** The URL of the image to show. */
  src: string;

  /** The alt text of the image. */
  alt?: string;

  /** Whether the image should be lazy-loaded. */
  lazy?: boolean;

  /** Any additional class names to be placed on the container element. */
  className?: string;

  /** The pixel distance from the top of the image to start loading the image. */
  offset?: number;

  /** The img tag's width. */
  width?: number;

  /** The img tag's width. */
  height?: number;

  /** The width to resize the image to via Cloudflare's image resizing API. */
  resizeWidth?: number;

  /** The height to resize the image to via Cloudflare's image resizing API. */
  resizeHeight?: number;

  /** The fit strategy to use when resizing the image. */
  resizeFit?: 'min' | 'crop';

  /**
   * The aspect ratio to use for the image. If not set, the image will not conform to
   * a specific ratio and may cause content re-flow.
   */
  aspect?: [number, number];

  /** The border radius of the image. */
  corner?: Corner;

  /**
   * The background colour of the image.
   *
   * @default 'transparent'
   */
  color?: Color;

  /**
   * The border colour of the image.
   *
   * @default 'transparent'
   */
  border?: Color;

  /** Whether the image is being displayed in AMP. */
  amp?: boolean;
}

const placeholder = (aspect: [number, number] | undefined) =>
  aspect
    ? `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${aspect[0]} ${aspect[1]} '%3E%3C/svg%3E`
    : `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=`;

interface LazyImgProps {
  className?: string;
  src: string;
  width?: number;
  height?: number;
  alt?: string;
  style?: object;
  aspect?: [number, number];
}

// An img tag that can handle an after-load placeholder swap for lazy images.
const LazyImg = ({
  src,
  aspect,

  ...props
}: LazyImgProps) => {
  const [currentSrc, setCurrentSrc] = useState(placeholder(aspect));

  useEffect(() => {
    const img = new window.Image();

    img.src = src;
    img.onload = () => setCurrentSrc(src);
  }, [src]);

  return <img src={currentSrc} {...props} />;
};

/**
 * Displays an image. This should be used for any displayed
 * images as it can handle lazy-loading and aspect ratios for you.
 */
export const Image = (props: ImageProps) => {
  const {
    height,
    width,
    alt,
    src,
    className,
    offset,
    lazy,
    resizeWidth,
    resizeHeight,
    resizeFit,
    aspect,
    amp,
  } = props;
  const styles = useStyles(imageStyles(props));
  const resizedSrc = withQuery(
    src,
    pickBy(
      {
        w:
          resizeWidth ||
          (aspect && resizeHeight
            ? (resizeHeight * aspect[0]) / aspect[1]
            : undefined),
        h:
          resizeHeight ||
          (aspect && resizeWidth
            ? (resizeWidth * aspect[1]) / aspect[0]
            : undefined),
        fit: resizeFit,
      },
      Boolean
    )
  );

  const [Img, imgProps] = amp
    ? ['amp-img' as any, { layout: width && height ? 'fixed' : 'responsive' }]
    : lazy
    ? [
        LazyImg,
        {
          className: cx('Image', className, styles.image),
          style: { width, height },
          aspect,
        },
      ]
    : [
        'img' as any,
        {
          className: cx('Image', className, styles.image),
          style: { width, height },
        },
      ];

  const [Container, containerProps] = lazy
    ? [
        LazyLoad,
        {
          once: true,
          offset,
          placeholder: (
            <Img
              {...imgProps}
              alt={alt}
              src={placeholder(aspect)}
              width={width}
              height={height}
              style={{ width, height }}
            />
          ),
        },
      ]
    : [React.Fragment, {}];

  return (
    <Container {...containerProps}>
      <Img
        {...imgProps}
        src={resizedSrc}
        width={width}
        height={height}
        alt={alt}
        style={{ width, height }}
      />
    </Container>
  );
};

export default Image;
